/**
 * Algorithm Selector Component
 *
 * Dropdown component for selecting prediction algorithms.
 * Shows algorithm name, description, and indicates advanced algorithms.
 */

import {
  Activity,
  BarChart3,
  Brain,
  Check,
  ChevronDown,
  TrendingUp,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import type { AlgorithmMetadata } from "../lib/algorithms";

// Map algorithm icon names to Lucide components
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Activity,
  BarChart3,
  Brain,
  TrendingUp,
  Zap,
};

interface AlgorithmSelectorProps {
  algorithms: AlgorithmMetadata[];
  selectedId: string;
  onSelect: (id: string) => void;
  disabled?: boolean;
  className?: string;
}

export function AlgorithmSelector({
  algorithms,
  selectedId,
  onSelect,
  disabled = false,
  className,
}: AlgorithmSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Find selected algorithm
  const selectedAlgorithm = algorithms.find((a) => a.id === selectedId);
  const SelectedIcon = selectedAlgorithm
    ? (ICON_MAP[selectedAlgorithm.icon] ?? TrendingUp)
    : TrendingUp;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm hover:bg-muted/70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <SelectedIcon className="h-4 w-4 text-primary flex-shrink-0" />
        <span className="font-medium flex-1 text-left truncate">
          {selectedAlgorithm?.name ?? m.f1_algo_select()}
        </span>
        {selectedAlgorithm?.isAdvanced && (
          <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-primary text-primary-foreground rounded">
            {m.f1_algo_badge_ai()}
          </span>
        )}
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-80 rounded-lg bg-card border border-border shadow-lg z-50 overflow-hidden">
          <div className="p-1">
            {algorithms.map((algorithm) => {
              const Icon = ICON_MAP[algorithm.icon] ?? TrendingUp;
              const isSelected = algorithm.id === selectedId;

              return (
                <button
                  key={algorithm.id}
                  type="button"
                  onClick={() => {
                    onSelect(algorithm.id);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2.5 rounded-md transition-colors flex items-start gap-3 ${
                    isSelected
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted/50"
                  }`}
                >
                  <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">
                        {algorithm.name}
                      </span>
                      {algorithm.isAdvanced && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-primary text-primary-foreground rounded">
                          {m.f1_algo_badge_advanced()}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {algorithm.description}
                    </p>
                  </div>
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact algorithm info display for the settings panel.
 */
interface AlgorithmInfoProps {
  algorithm: AlgorithmMetadata | undefined;
}

export function AlgorithmInfo({ algorithm }: AlgorithmInfoProps) {
  if (!algorithm) {
    return (
      <div className="text-sm text-muted-foreground">
        {m.f1_algo_no_selection()}
      </div>
    );
  }

  const Icon = ICON_MAP[algorithm.icon] ?? TrendingUp;

  return (
    <div className="flex items-start gap-3">
      <div className="p-2 rounded-lg bg-primary/10">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{algorithm.name}</span>
          {algorithm.isAdvanced && (
            <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-primary text-primary-foreground rounded">
              {m.f1_algo_badge_ai()}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {algorithm.description}
        </p>
      </div>
    </div>
  );
}
